/**
 * SubBarWidget — chart_type "sub_bar".
 *
 * One group per payer. Inside a group each PA type gets its own bar, and the
 * group closes with an Overall chip.
 *
 * Bar length encodes CASE VOLUME (each series' `value`), not the percentage —
 * the two disagree constantly (a payer can post 100% on a handful of cases),
 * and length-by-volume is what makes a thin-evidence row read as thin. The
 * rate is text beside the bar, and the header says so outright rather than
 * leaving the encoding to be inferred. Bars scale to the largest `value` in
 * the payload, so lengths compare across every group in this widget.
 *
 * Rates are suppressed, not rounded, when the backend says so. A series with
 * `insufficient_data: true` sends `percentage: null` because too few outcomes
 * were decided (`metadata.min_decided_for_rate`), and printing 0% or blank
 * there would both read as real findings. The bar still renders at full
 * strength — the case volume IS known; only the rate isn't.
 *
 * `overall.percentage` is backend-supplied and deliberately not derived: it
 * is neither the sum nor the mean of the series (HUMANA's parts cover 100 of
 * 112 outcomes but overall is 98/110) because a case needing both a medical
 * pre-cert and a pharmacy PA is counted once. Only the backend has that
 * de-duplicated set.
 */

import { JSX, useMemo, useState } from "react";

import WidgetPlaceholder from "./WidgetPlaceholder";
import DownloadOptionsMenu, {
  type WidgetDownloadOptions,
} from "./DownloadOptionsMenu";

// ─── Series ───────────────────────────────────────────────────────────────────

// The payload names its measures as fixed keys rather than sending a series
// list, so labels and colours live here. A third measure means one more row
// in this array and nothing else.
const SERIES = [
  { key: "medical_precert", label: "Med Pre-Cert", color: "#1f77d0" },
  { key: "pharmacy_pa", label: "Pharmacy PA", color: "#ef6c2e" },
  { key: "admin", label: "Admin", color: "#5D3FD3" },
] as const;

type SeriesKey = (typeof SERIES)[number]["key"];

// ─── Payload ──────────────────────────────────────────────────────────────────

type SeriesCell = {
  /** null when `insufficient_data` — too few decided outcomes to state a rate. */
  percentage?: number | null;
  /** Decided outcomes for this measure; drives the bar length and the n= text. */
  value?: number | null;
  insufficient_data?: boolean;
};

type SubBarRow = {
  name?: string;
  /** Plan category this payer belongs to (Medicare, Commercial, …). Optional:
   *  earlier payloads omit it entirely, in which case no filter is offered. */
  insurance_type?: string | null;
  /** The payer's total case volume. */
  value?: number | null;
  /** Decided PA outcomes across both measures, de-duplicated. */
  decided?: number | null;
  insufficient_data?: boolean;
  overall?: { percentage?: number | null } | null;
} & Partial<Record<SeriesKey, SeriesCell | null>>;

type Metadata = {
  ranked_by?: string;
  total_payers?: number;
  payers_returned?: number;
  /** Decided outcomes a payer needs before a rate is reported at all. */
  min_decided_for_rate?: number;
};

type SubBarResponse = {
  data?: SubBarRow[];
  metadata?: Metadata;
  // Siblings of `.data`, not nested under `.metadata`.
  has_download_options?: boolean;
  download_options?: WidgetDownloadOptions;
};

type SubBarWidgetProps = {
  isLoading: boolean;
  data: unknown;
  title?: string;
  dashboardName?: string;
  widgetTemplateId?: string | number;
  appliedFilters?: Record<string, unknown>;
};

// Every field below is read defensively, so a structural narrow stands in for
// a full zod parse — same approach as the sibling widgets in this folder.
const unwrap = (raw: unknown): SubBarResponse =>
  raw && typeof raw === "object" ? (raw as SubBarResponse) : {};

const isNumeric = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);

const formatPercent = (v: unknown): string =>
  isNumeric(v) ? `${v.toFixed(1)}%` : "—";

const formatCount = (v: unknown): string =>
  isNumeric(v) ? v.toLocaleString() : "—";

// ─── Component ────────────────────────────────────────────────────────────────

export default function SubBarWidget({
  isLoading,
  data,
  title,
  dashboardName,
  widgetTemplateId,
  appliedFilters,
}: SubBarWidgetProps): JSX.Element {
  const parsed = useMemo(() => unwrap(data), [data]);
  const rows = useMemo(() => parsed.data ?? [], [parsed]);
  const meta = parsed.metadata;

  // null = no filter, show every payer. Tracked by the type's own string
  // because that is what the rows carry — an index would go stale the moment
  // a refetch reorders them.
  const [insuranceType, setInsuranceType] = useState<string | null>(null);

  // Distinct plan categories in payload order (which is volume-desc), so the
  // dropdown reads biggest-first like the list under it rather than being
  // re-sorted into an order nothing else on screen uses.
  const insuranceTypes = useMemo(() => {
    const seen: string[] = [];
    rows.forEach((row) => {
      const type =
        typeof row.insurance_type === "string" ? row.insurance_type.trim() : "";
      if (type && !seen.includes(type)) seen.push(type);
    });
    return seen;
  }, [rows]);

  // A stale selection (the type vanished after a refetch) falls back to
  // showing everything rather than an empty list with no way out.
  const activeType =
    insuranceType !== null && insuranceTypes.includes(insuranceType)
      ? insuranceType
      : null;

  const visibleRows = useMemo(
    () =>
      activeType === null
        ? rows
        : rows.filter((row) => row.insurance_type === activeType),
    [rows, activeType],
  );

  // Only the measures the backend actually sent. A key that is absent from
  // every row is dropped from the legend AND from each payer's rows — the
  // alternative was a permanent "Admin — n=—" line on every payer for a
  // response that never mentioned Admin at all.
  //
  // Derived from ALL rows, not the filtered ones, so the legend doesn't
  // appear and disappear as the insurance-type filter changes.
  //
  // `!== undefined` rather than a truthiness check: JSON cannot produce an
  // explicit `undefined`, so anything defined means the backend sent the key.
  // A sent-but-empty measure (`"admin": {"value": 0}`) is real data and stays
  // visible; only a genuinely missing key is hidden.
  const visibleSeries = useMemo(
    () => SERIES.filter((series) => rows.some((row) => row[series.key] !== undefined)),
    [rows],
  );

  // Aggregate for the group currently on screen — it reads from visibleRows,
  // so switching the insurance-type filter re-states it for that plan type
  // rather than always describing the whole payload.
  //
  // The rate is VOLUME-WEIGHTED (total type cases / total cases), not the
  // mean of each payer's percentage: averaging percentages would give a payer
  // with 3 cases the same say as one with 3,789, which reads wildly off the
  // real group rate. Both parts are integer sums, so nothing is lost to
  // rounding the way a mean of already-rounded percentages would be.
  //
  // The Overall figure IS aggregatable, despite the de-duplication. A payer's
  // `overall.percentage` already counts a case needing both a medical
  // pre-cert and a pharmacy PA once; payers are disjoint sets of cases, so
  // those per-payer numerators sum cleanly across the group. Recovered as
  // `overall% x cases` per payer, then divided by total cases. `percentage`
  // arrives at 1dp, which bounds the drift at +/-0.05pp -- immaterial next to
  // a figure shown to one decimal.
  const summary = useMemo(() => {
    let totalCases = 0;
    let overallCases = 0;
    const perSeries = new Map<string, number>();
    visibleRows.forEach((row) => {
      if (isNumeric(row.value)) {
        totalCases += row.value;
        const overallPercent = row.overall?.percentage;
        if (isNumeric(overallPercent)) {
          overallCases += (overallPercent / 100) * row.value;
        }
      }
      visibleSeries.forEach(({ key }) => {
        const value = row[key]?.value;
        if (isNumeric(value)) {
          perSeries.set(key, (perSeries.get(key) ?? 0) + value);
        }
      });
    });
    const series = visibleSeries.map(({ key, label, color }) => {
      const cases = perSeries.get(key) ?? 0;
      return {
        key,
        label,
        color,
        cases,
        percentage: totalCases > 0 ? (cases / totalCases) * 100 : null,
      };
    });
    return {
      totalCases,
      payerCount: visibleRows.length,
      series,
      overallPercent:
        totalCases > 0 ? (overallCases / totalCases) * 100 : null,
    };
  }, [visibleRows, visibleSeries]);

  // Longest bar among the VISIBLE rows, so filtering to a small plan category
  // still fills the track instead of leaving every bar a sliver measured
  // against some payer that is no longer on screen. Guarded to 1 so an
  // all-zero payload can't divide by zero and produce NaN widths.
  const maxValue = useMemo(() => {
    let max = 0;
    visibleRows.forEach((row) =>
      visibleSeries.forEach(({ key }) => {
        const value = row[key]?.value;
        if (isNumeric(value) && value > max) max = value;
      }),
    );
    return max > 0 ? max : 1;
  }, [visibleRows, visibleSeries]);

  const downloadMenu = (
    <DownloadOptionsMenu
      menuId={
        widgetTemplateId !== undefined ? String(widgetTemplateId) : undefined
      }
      title={title}
      dashboardName={dashboardName}
      widgetTemplateId={widgetTemplateId}
      appliedFilters={appliedFilters}
      downloadOptions={
        parsed.has_download_options ? parsed.download_options : undefined
      }
    />
  );

  if (isLoading) return <WidgetPlaceholder height={240} />;

  if (rows.length === 0) {
    return (
      <div className="sub-bar">
        {downloadMenu}
        <p className="text-muted small mb-0">No data available.</p>
      </div>
    );
  }

  const minDecided = meta?.min_decided_for_rate;

  return (
    <div className="sub-bar">
      {downloadMenu}
      <div className="sub-bar__head">
        <div className="sub-bar__legend">
          {visibleSeries.map((s) => (
            <span key={s.key} className="sub-bar__legend-item">
              <span
                className="sub-bar__swatch"
                style={{ background: s.color }}
                aria-hidden="true"
              />
              {s.label}
            </span>
          ))}
        </div>
        <div className="sub-bar__head-right">
          {/* Only offered when the payload actually distinguishes plan
              categories: one type (or none, on the older payload) makes the
              control a no-op that just takes up room. */}
          {insuranceTypes.length > 1 && (
            <select
              className="form-select form-select-sm sub-bar__filter"
              value={activeType ?? ""}
              onChange={(event) => setInsuranceType(event.target.value || null)}
              aria-label="Filter payers by insurance type"
            >
              <option value="">Plan Types</option>
              {insuranceTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          )}
          {/* States the encoding outright: length is volume, the number is
              the rate. Without it the two read as the same measure. */}
          <span className="sub-bar__caption">Bar length = case volume</span>
        </div>
      </div>

      <p className="sub-bar__note">
        {isNumeric(minDecided) && (
          <span>
            Rates shown only where at least {minDecided} PA outcomes were
            decided.
          </span>
        )}
      </p>
      <div className="sub-bar__list">
        {/* Group summary. Deliberately NOT shaped like a payer row: no bars
            and no per-row Overall chip, because these totals dwarf any single
            payer and sharing that layout invited them to be read on the same
            scale. Two headline figures, then the per-type split as chips. */}
        {visibleRows.length > 0 && (
          <div className="sub-bar__summary">
            <div className="sub-bar__summary-head">
              <span className="sub-bar__summary-title">Summary</span>
              <span className="sub-bar__summary-scope">
                {summary.payerCount.toLocaleString()}{" "}
                {summary.payerCount === 1 ? "payer" : "payers"}
              </span>
            </div>

            <div className="sub-bar__summary-body">
              <div className="sub-bar__summary-stats">
                <div className="sub-bar__stat">
                  <span className="sub-bar__stat-value">
                    {formatCount(summary.totalCases)}
                  </span>
                  <span className="sub-bar__stat-label">Total cases</span>
                </div>
                <div className="sub-bar__stat">
                  <span className="sub-bar__stat-value">
                    {formatPercent(summary.overallPercent)}
                  </span>
                  <span className="sub-bar__stat-label">Overall</span>
                </div>
              </div>

              <div className="sub-bar__summary-types">
                {summary.series.map((entry) => (
                  <span className="sub-bar__type" key={entry.key}>
                    <span
                      className="sub-bar__swatch"
                      style={{ background: entry.color }}
                      aria-hidden="true"
                    />
                    <span className="sub-bar__type-label">{entry.label}</span>
                    <span className="sub-bar__type-pct">
                      {formatPercent(entry.percentage)}
                    </span>
                    <span className="sub-bar__type-n">
                      n={formatCount(entry.cases)}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {visibleRows.length === 0 && (
          <p className="text-muted small mb-0">
            No payers for this insurance type.
          </p>
        )}
        {visibleRows.map((row, index) => (
          <div className="sub-bar__group" key={`${row.name ?? "row"}-${index}`}>
            <div className="sub-bar__payer">
              <span className="sub-bar__payer-name">{row.name ?? "—"}</span>
              <span className="sub-bar__payer-meta">
                {formatCount(row.value)} cases
                {isNumeric(row.decided)
                  ? ` · ${formatCount(row.decided)} decided`
                  : ""}
              </span>
            </div>

            <div className="sub-bar__group-body">
              <div className="sub-bar__series">
                {visibleSeries.map((series) => {
                  const cell = row[series.key];
                  const rawValue = cell?.value;
                  const value = isNumeric(rawValue) ? rawValue : null;
                  const suppressed = !isNumeric(cell?.percentage);
                  // A missing value means no bar at all, which reads
                  // differently from a genuine zero-length one.
                  const width =
                    value === null ? 0 : Math.max((value / maxValue) * 100, 0);
                  return (
                    <div className="sub-bar__row" key={series.key}>
                      <span className="sub-bar__label">{series.label}</span>
                      <span className="sub-bar__track">
                        <span
                          className="sub-bar__fill"
                          style={{
                            width: `${width}%`,
                            background: series.color,
                          }}
                        />
                      </span>
                      <span
                        className={`sub-bar__pct${
                          suppressed ? " sub-bar__pct--muted" : ""
                        }`}
                        title={
                          suppressed && isNumeric(minDecided)
                            ? `Too few decided outcomes to report a rate (needs ${minDecided}).`
                            : undefined
                        }
                      >
                        {formatPercent(cell?.percentage)}
                      </span>
                      <span className="sub-bar__n">
                        n={formatCount(cell?.value)}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="sub-bar__overall">
                <span className="sub-bar__overall-label">Overall</span>
                <span
                  className={`sub-bar__overall-value${
                    isNumeric(row.overall?.percentage)
                      ? ""
                      : " sub-bar__overall-value--muted"
                  }`}
                >
                  {formatPercent(row.overall?.percentage)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
